import { DependencyList, MutableRefObject, useEffect } from 'react';

export type TextAreaSizeControlType = 'auto' | 'strict' | 'max' | 'min';

export interface TextAreaAutosizeOptions {
    /**
     * Ссылка на textarea-элемент
     */
    ref: MutableRefObject<HTMLTextAreaElement | HTMLDivElement | null>;

    /**
     * Тип автоматического растягивания контейнера для `textarea`:
     * - auto - контейнер будет меняться в зависимости от высоты контента (до бесконечности)
     * - max - контейнер будет меняться, пока не достигнет максимального количества строк,
     * определенного параметром `rows`. Далее будет доступен scroll для просмотра всего контента
     * - min - контейнер всегда имеет минимум `rows` строк, далее расширяется в зависимости от высоты контента
     * - strict - контейнер не будет растягиваться, а сразу получит максимальное количество строк,
     * определенное параметром `rows`. Далее будет доступен scroll для просмотра всего контента
     * @default max
     */
    sizeControlType?: TextAreaSizeControlType;

    /**
     * Количество строк для `textarea`
     * Позволяет контролировать максимальное растягивание `textarea`
     * @default 1
     */
    rows?: number;

    /**
     * Признак включения автоматического изменения размера `textarea`
     * @default true
     */
    isEnabled?: boolean;
}

/**
 * React Hook для применения автоматического изменения размера `textarea`
 * Задает свойство `height` в соответствии с высотой строки
 *
 * @param options - параметры растягивания textArea
 * @param deps - зависимости для обновления textArea (например, value)
 */
export const useTextAreaAutosize = (options: TextAreaAutosizeOptions, deps: DependencyList): void => {
    const { ref: textAreaRef, sizeControlType, rows = 1, isEnabled = true } = options;

    useEffect(() => {
        if (!isEnabled || !textAreaRef.current) return;

        // высота строки для текста для подсчёта корректной высоты textarea
        const lineHeight = parseInt(window.getComputedStyle(textAreaRef.current).getPropertyValue('line-height'));

        let textAreaHeight: number;

        switch (sizeControlType) {
            // если минимальная высота строки превышается - контейнер растягивается на высоту textarea
            // в противном случае - возвращается на исходное значение высоты сроки
            case 'auto':
                // сперва нужно сбросить значение высоты до минимального, потом поставить новое
                // (без сброса не будет стягивания обратно при удалении текста)
                if (textAreaRef.current instanceof HTMLTextAreaElement) {
                    textAreaRef.current.style.height = `${lineHeight}px`;

                    textAreaHeight = Math.max(textAreaRef.current.scrollHeight, lineHeight);
                    textAreaRef.current.style.height = `${textAreaHeight}px`;
                } else {
                    textAreaRef.current.style.height = 'auto';
                }
                break;

            // если минимальная высота строки превышается - контейнер растягивается на высоту textarea,
            // ограниченную параметром textAreaRows
            // в противном случае - возвращается на исходное значение высоты сроки
            case 'max':
                // сперва нужно сбросить значение высоты до минимального, потом поставить новое
                // (без сброса не будет стягивания обратно при удалении текста)
                if (textAreaRef.current instanceof HTMLTextAreaElement) {
                    textAreaRef.current.style.height = `${lineHeight}px`;

                    textAreaHeight = Math.min(textAreaRef.current.scrollHeight, lineHeight * rows);
                    textAreaRef.current.style.height = `${textAreaHeight}px`;
                } else {
                    textAreaRef.current.style.height = 'auto';
                    textAreaRef.current.style.maxHeight = `${lineHeight * rows}px`;
                }
                break;

            case 'min':
                textAreaRef.current.style.minHeight = `${lineHeight * rows}px`;
                textAreaRef.current.style.height = 'auto';
                break;

            // если контейнер всегда равен высоте textarea rows - он должен всегда быть растянутым
            case 'strict':
                textAreaRef.current.style.height = `${lineHeight * rows}px`;
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO DIRECT-222878 Указать причину
    }, [isEnabled, rows, sizeControlType, textAreaRef, ...deps]);
};
